import { CASCADING, CONNECTION, CONNECTION_POINTS_EDITING_SNAP, Cursors } from '../../../constants';
import { Point } from '../../../math';
import { deepExtend } from '../../../utils/deep-extend';
import { Circle } from '../../../svg';
import { AdornerBase } from '../AdornerBase';
import { ConnectionDragUnit } from '../../ConnectionDragUnit';
import { buildConnection, simplifyOrthogonalPoints } from '../../routing/CascadingRouting';
import { normalizeLocks } from './normalizeLocks';
import { findSegmentIndexFromHandlePoint } from './segmentIndex';
import { pruneRedundantLocks } from './pruneRedundantLocks';
function getAxisFromSegment(seg) {
    // horizontal segment => move Y; vertical => move X
    return seg.orientation === 'H' ? 'y' : 'x';
}
function pointOnOrthoSegment(p, a, b, tol) {
    if (a.y === b.y) {
        const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
        return Math.abs(p.y - a.y) <= tol && p.x >= minX - tol && p.x <= maxX + tol;
    }
    const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
    return Math.abs(p.x - a.x) <= tol && p.y >= minY - tol && p.y <= maxY + tol;
}
export class CascadingConnectionResizeAdorner extends AdornerBase {
    constructor(connection, options) {
        super(connection.diagram, deepExtend({}, options));
        this._sourceSide = null;
        this._targetSide = null;
        this.connection = connection;
        this.handles = [];
        this.activeHandle = null;
        this._firstMove = false;
        if (options) {
            this._createHandles();
        }
        this.diagram.bind('dblclick', this.onDblClick.bind(this));
    }
    onDblClick(e) {
        const handle = e.item.name === CONNECTION && this._hitTest(e.point);
        if (!handle || !handle.vertex) {
            return;
        }
        const segmentsMeta = this.connection.route.segmentsMeta;
        const segmentIndex = findSegmentIndexFromHandlePoint(handle.point, segmentsMeta, { tolerance: 8 });
        const seg = segmentsMeta[segmentIndex];
        if (seg && seg.ownerLockIndex != null) {
            this.connection.locks.splice(seg.ownerLockIndex, 1);
            this.connection.routingMode = this.connection.locks.length ? 'manual' : 'auto';
            const route = buildConnection({ ...this.connection.buildOptions, locks: this.connection.locks });
            const source = this.connection.sourcePoint();
            const target = this.connection.targetPoint();
            const renderPoints = simplifyOrthogonalPoints([source, ...route.points, target]);
            this.connection.points(renderPoints.slice(1, -1));
            this.connection.route = route;
            this.connection.refresh();
            this.refresh();
        }
    }
    createMiddlePoints() {
        var _a;
        const route = this.connection.route;
        const result = [];
        if (!route) {
            return result;
        }
        const points = route.points;
        if (points.length < 2)
            return result;
        const pointsForHandles = this._axisAligned(points) ? [points[0], points[points.length - 1]] : points;
        const all = this.connection.allPoints(); // includes source & target
        const source = all[0];
        const target = all[all.length - 1];
        const firstAfterSource = all.length > 2 ? all[1] : null;
        const lastBeforeTarget = all.length > 2 ? all[all.length - 2] : null;
        const anchorOffset = (_a = this.connection.options.anchorOffset) !== null && _a !== void 0 ? _a : 0;
        const eps = 0.5;
        for (let i = 0; i < pointsForHandles.length - 1;) {
            let j = i + 1;
            while (j < pointsForHandles.length - 1 && this._axisAligned(pointsForHandles.slice(i, j + 2))) {
                j++;
            }
            const pStart = pointsForHandles[i];
            const pEnd = pointsForHandles[j];
            if (pStart.x === pEnd.x && pStart.y === pEnd.y) {
                i = j;
                continue;
            }
            let midPoint = new Point((pStart.x + pEnd.x) / 2, (pStart.y + pEnd.y) / 2);
            const isFirstSegment = i === 0;
            const isLastSegment = j === pointsForHandles.length - 1;
            const startAligned = isFirstSegment && firstAfterSource && this._axisAligned([source, midPoint, firstAfterSource]);
            const endAligned = isLastSegment && lastBeforeTarget && this._axisAligned([target, midPoint, lastBeforeTarget]);
            const skipStart = startAligned && firstAfterSource && source.distanceTo(firstAfterSource) <= anchorOffset + eps;
            const skipEnd = endAligned && lastBeforeTarget && target.distanceTo(lastBeforeTarget) <= anchorOffset + eps;
            if (skipStart || skipEnd) {
                i = j;
                continue;
            }
            const fixStart = isFirstSegment && startAligned && !skipStart;
            const fixEnd = isLastSegment && endAligned && !skipEnd;
            if (fixStart && firstAfterSource) {
                midPoint = new Point((source.x + firstAfterSource.x) / 2, (source.y + firstAfterSource.y) / 2);
            }
            else if (fixEnd && lastBeforeTarget) {
                midPoint = new Point((target.x + lastBeforeTarget.x) / 2, (target.y + lastBeforeTarget.y) / 2);
            }
            result.push(midPoint);
            i = j;
        }
        return result;
    }
    _createHandles() {
        this.handles.forEach((h) => this.visual.remove(h.visual));
        this.handles = [];
        if ((this.connection.options.type || '').toLowerCase() !== CASCADING || this.connection.pointsEditable() === false) {
            return;
        }
        const midPoints = this.createMiddlePoints();
        const handles = this.options;
        const lockedSegments = this.connection.route.segmentsMeta.filter(s => s.ownerLockIndex !== null);
        midPoints.forEach((pt) => {
            const vertex = lockedSegments.some(s => pointOnOrthoSegment(pt, s.a, s.b, 0.5));
            const handleVisual = new Circle({
                center: this.diagram.modelToLayer(pt),
                radius: vertex ? handles.vertex.radius : handles.midpoint.radius,
                fill: vertex ? handles.vertex.fill : handles.midpoint.fill,
                stroke: vertex ? handles.vertex.stroke : handles.midpoint.stroke
            });
            this.visual.append(handleVisual);
            this.handles.push({
                visual: handleVisual,
                point: pt,
                vertex
            });
        });
    }
    _axisAligned(points) {
        if (points.length <= 1) {
            return true;
        }
        const { x, y } = points[0];
        return points.every((p) => p.x === x) || points.every((p) => p.y === y);
    }
    _getCursor(point) {
        if (this._activeDrag) {
            return this._activeDrag.cursor;
        }
        const handle = point && this._hitTest(point);
        if (handle) {
            const segmentsMeta = this.connection.route.segmentsMeta;
            const segmentIndex = findSegmentIndexFromHandlePoint(handle.point, segmentsMeta, { tolerance: 8 });
            const seg = segmentsMeta[segmentIndex];
            return !seg || seg.orientation === 'H' ? Cursors.rowresize : Cursors.colresize;
        }
    }
    _hitTest(point) {
        var _a;
        const handleOptions = this.options;
        if (!handleOptions.midpoint || !handleOptions.vertex) {
            return null;
        }
        const hitDistance = handleOptions.vertex.radius + (((_a = handleOptions.vertex.stroke) === null || _a === void 0 ? void 0 : _a.width) || 1);
        // Scale hit distance by zoom level (handle sizes are in layer pixels, but we compare in model space)
        const zoom = this.diagram.zoom();
        const scaledHitDistance = hitDistance / zoom;
        return this.handles.find((h) => h.point.distanceTo(point) <= scaledHitDistance) || null;
    }
    start(p) {
        this.activeHandle = this._hitTest(p);
        if (this.activeHandle) {
            // Store initial locks for undo/redo
            this._initialLocks = this.connection.locks.map((lock) => ({ ...lock }));
            this._routingMode = this.connection.routingMode;
            this._sourceSide = this.connection.buildOptions.sourceSide;
            this._targetSide = this.connection.buildOptions.targetSide;
            const segmentsMeta = this.connection.route.segmentsMeta;
            const segmentIndex = findSegmentIndexFromHandlePoint(p, segmentsMeta, { tolerance: 8 });
            const seg = segmentsMeta[segmentIndex];
            if (!seg) {
                this._activeDrag = {
                    axis: 'y',
                    lockIndex: null,
                    insertIndex: this.connection.locks.length,
                    inserted: false,
                    cursor: Cursors.arrow,
                    segmentIndex
                };
            }
            else {
                this._activeDrag = {
                    axis: getAxisFromSegment(seg),
                    lockIndex: seg.ownerLockIndex,
                    insertIndex: seg.insertIndex,
                    inserted: false,
                    cursor: !seg || seg.orientation === 'H' ? Cursors.rowresize : Cursors.colresize,
                    segmentIndex
                };
            }
            this._firstMove = true;
        }
    }
    move(_handle, p) {
        if (!this.activeHandle) {
            return false;
        }
        const pointer = new Point(Math.round(p.x), Math.round(p.y));
        const connection = this.connection;
        const active = this._activeDrag;
        let value = active.axis === 'x' ? pointer.x : pointer.y;
        // Try to snap to nearby parallel segments
        const snapValue = this._findSnapValue(active.axis, value);
        if (snapValue !== null) {
            value = snapValue;
        }
        // 1) if we don't have a lock for editing -> insert once
        if (active.lockIndex == null && !active.inserted) {
            connection.locks.splice(active.insertIndex, 0, { axis: active.axis, value });
            active.lockIndex = active.insertIndex;
            active.inserted = true;
        }
        // 2) edit
        if (active.lockIndex != null) {
            connection.locks[active.lockIndex].value = value;
        }
        const route = buildConnection({ ...this.connection.buildOptions, locks: connection.locks });
        this.updateConnection(route);
        return true;
    }
    updateConnection(route) {
        this.connection.routingMode = 'manual';
        const source = this.connection.sourcePoint();
        const target = this.connection.targetPoint();
        const renderPoints = simplifyOrthogonalPoints([source, ...route.points, target]);
        this.connection.points(renderPoints.slice(1, -1));
        this.connection.route = route;
        // Update the path with routing to keep everything in sync
        this.connection.refresh();
        // Hide all handles during drag for cleaner UX
        if (this._firstMove && this.handles) {
            this.handles.forEach((handle) => {
                if (handle.visual) {
                    handle.visual.visible(false);
                }
            });
        }
        this._firstMove = false;
    }
    /**
     * Find a snap value by checking adjacent parallel segments in the same connection
     * @param axis - The axis we're dragging on ('x' or 'y')
     * @param currentValue - The current value on that axis
     * @returns The snap value if found, or null if no snap
     */
    _findSnapValue(axis, currentValue) {
        var _a;
        const segments = (_a = this.connection.route) === null || _a === void 0 ? void 0 : _a.segmentsMeta;
        if (!segments || !this._activeDrag) {
            return null;
        }
        const activeLockIndex = this._activeDrag.lockIndex;
        let closestValue = null;
        let closestDistance = this.options.snap !== undefined ? this.options.snap : CONNECTION_POINTS_EDITING_SNAP;
        for (const segment of segments) {
            // Skip the segment being dragged (if it has a lock)
            if (activeLockIndex !== null && segment.ownerLockIndex === activeLockIndex) {
                continue;
            }
            // For Y-axis drag (horizontal segment), look for other horizontal segments
            // For X-axis drag (vertical segment), look for other vertical segments
            const isParallel = (axis === 'y' && segment.orientation === 'H') ||
                (axis === 'x' && segment.orientation === 'V');
            if (!isParallel) {
                continue;
            }
            // Get the constant coordinate of this parallel segment
            const segmentValue = segment.orientation === 'H'
                ? (segment.a.y + segment.b.y) / 2
                : (segment.a.x + segment.b.x) / 2;
            const distance = Math.abs(segmentValue - currentValue);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestValue = segmentValue;
            }
        }
        return closestValue;
    }
    stop(_p) {
        var _a, _b;
        if (!this.activeHandle) {
            return undefined;
        }
        const previousLocks = this.connection.locks.map(l => ({ ...l }));
        this.connection.locks = normalizeLocks(this.connection.locks, this.connection.route.connectionStart);
        const source = this.connection.sourcePoint();
        const target = this.connection.targetPoint();
        this.connection.locks = pruneRedundantLocks(this.connection.locks, this.connection.buildOptions, {
            tolerance: 0.5
        });
        const buildOptions = { ...this.connection.buildOptions };
        const connectors = {};
        this.connection._resolveConnectors(connectors);
        if (!((_a = connectors._resolvedTargetConnector) === null || _a === void 0 ? void 0 : _a.positionWithoutOffset().equals(target)) ||
            !((_b = connectors._resolvedSourceConnector) === null || _b === void 0 ? void 0 : _b.positionWithoutOffset().equals(source))) {
            // Revert to previous locks if normalize or pruning caused connector change
            this.connection.locks = previousLocks;
            this.connection.buildOptions = buildOptions;
        }
        const route = buildConnection({ ...this.connection.buildOptions, locks: this.connection.locks });
        this.updateConnection(route);
        const unit = new ConnectionDragUnit({
            item: this.connection,
            undoLocks: this._initialLocks || [],
            redoLocks: this.connection.locks,
            undoPoints: [],
            redoPoints: [],
            routingMode: this._routingMode,
            sourceSide: this._sourceSide,
            targetSide: this._targetSide
        });
        // Clear active handle BEFORE refresh() to prevent it from being reassigned
        this._resetFlags();
        // Refresh handles to recreate them based on the new connection shape
        this.refresh();
        return unit;
    }
    refresh() {
        this._createHandles();
    }
    cancel() {
        if (this.activeHandle && this._initialLocks) {
            // Restore initial locks
            if (this._initialLocks) {
                this.connection.locks = this._initialLocks.map((lock) => ({ ...lock }));
                this.updateConnection(buildConnection({ ...this.connection.buildOptions, locks: this.connection.locks }));
            }
            this.refresh();
            this._resetFlags();
        }
    }
    _resetFlags() {
        this.activeHandle = null;
        this._activeDrag = null;
        this._firstMove = false;
        // Clean up initial locks reference
        if (this._initialLocks) {
            this._initialLocks = undefined;
        }
    }
}
